import React, { useMemo } from "react";

import SixMinuteWalkRouteChart from "../../charts/SixMinuteWalkRouteChart";
import { SignalChart } from "../../charts/SignalChart";
import { formatValidatedMeasurement } from "../../../utils/results/validatedRanges";
import {
  pickSixMinuteWalkCardiacQuality,
  pickSixMinuteWalkDetrendedSignal,
  pickSixMinuteWalkDistanceMeters,
  pickSixMinuteWalkHeartRate,
  pickSixMinuteWalkRecoveryDelayMs,
  pickSixMinuteWalkRespiratoryQuality,
  pickSixMinuteWalkRespiratoryRate,
  pickSixMinuteWalkRouteTrace,
  pickSixMinuteWalkTotalTrackedDistanceMeters,
} from "../../../utils/results/sixMinuteWalk";
import { getPointSeriesMaxSecs } from "../../../utils/results/viewTimeSeries";

function normalizeStatus(value) {
  if (typeof value !== "string") {
    return "unknown";
  }

  const normalized = value.trim().toLowerCase();

  if (
    normalized === "pass" ||
    normalized === "passed" ||
    normalized === "good" ||
    normalized === "very good"
  ) {
    return normalized === "passed" ? "pass" : normalized;
  }

  if (
    normalized === "fail" ||
    normalized === "failed" ||
    normalized === "bad" ||
    normalized === "poor"
  ) {
    return normalized === "failed" ? "fail" : normalized;
  }

  return "unknown";
}

function formatStatusValue(value, t) {
  const normalized = normalizeStatus(value);

  if (normalized === "very good") {
    return t("profilePage.statusVeryGood", "Very good");
  }

  if (normalized === "good") {
    return t("profilePage.statusGood", "Good");
  }

  if (normalized === "pass") {
    return t("profilePage.statusPass", "Pass");
  }

  if (normalized === "fail") {
    return t("profilePage.statusFail", "Fail");
  }

  if (normalized === "bad") {
    return t("profilePage.statusBad", "Bad");
  }

  return "--";
}

function formatDistanceValue(value) {
  return typeof value === "number" && Number.isFinite(value)
    ? value.toFixed(1)
    : "--";
}

function formatSecondsValue(valueMs) {
  return typeof valueMs === "number" && Number.isFinite(valueMs)
    ? String(Math.max(0, Math.round(valueMs / 1000)))
    : "--";
}

function buildPrefixedMetricLabel(t, prefix, metric) {
  return t("profilePage.prefixedMetricLabel", {
    prefix,
    metric,
    defaultValue: `${prefix} ${metric}`,
  });
}

function SectionCard({ title, children }) {
  return (
    <section className="rounded-xl border border-greyscale-1 bg-container p-4">
      <h3 className="text-lg font-semibold text-typography">{title}</h3>
      <div className="mt-4">{children}</div>
    </section>
  );
}

function SummaryCard({ label, value, unit }) {
  return (
    <div className="card min-w-[160px] px-3 py-2">
      <p className="text-sm text-greyscale-3">{label}</p>
      <div className="flex items-end gap-2">
        <p className="text-2xl font-semibold text-typography">{value}</p>
        {unit ? <p className="pb-1 text-sm text-greyscale-3">{unit}</p> : null}
      </div>
    </div>
  );
}

function SignalSection({ title, points, emptyLabel }) {
  if (!points.length) {
    return (
      <SectionCard title={title}>
        <div className="rounded-lg bg-background px-4 py-10 text-center text-sm text-greyscale-3">
          {emptyLabel}
        </div>
      </SectionCard>
    );
  }

  return (
    <SectionCard title={title}>
      <SignalChart
        rawSignal={[]}
        smoothSignal={points}
        showRawSignal={false}
        smoothBorderColor="#107A92"
        peaks={[]}
        troughs={[]}
        maxSecs={Math.max(1, Math.ceil(getPointSeriesMaxSecs(points, 60) || 0))}
        height={224}
        tickStepSize={
          points.length && getPointSeriesMaxSecs(points, 60) <= 10 ? 0.5 : 5
        }
      />
    </SectionCard>
  );
}

function SignalComparisonSection({
  title,
  preLabel,
  postLabel,
  prePoints,
  postPoints,
  emptyLabel,
}) {
  const hasPrePoints = prePoints.length > 0;
  const hasPostPoints = postPoints.length > 0;
  const maxSecs = Math.max(
    getPointSeriesMaxSecs(prePoints, 0),
    getPointSeriesMaxSecs(postPoints, 0),
  );

  if (!hasPrePoints && !hasPostPoints) {
    return (
      <SectionCard title={title}>
        <div className="rounded-lg bg-background px-4 py-10 text-center text-sm text-greyscale-3">
          {emptyLabel}
        </div>
      </SectionCard>
    );
  }

  return (
    <SectionCard title={title}>
      <div className="mb-3 flex flex-wrap gap-3 text-xs text-greyscale-3">
        {hasPrePoints ? (
          <div className="flex items-center gap-2">
            <span
              className="inline-block h-2.5 w-2.5 rounded-full"
              style={{ backgroundColor: "#B776B9" }}
            />
            <span>{preLabel}</span>
          </div>
        ) : null}
        {hasPostPoints ? (
          <div className="flex items-center gap-2">
            <span
              className="inline-block h-2.5 w-2.5 rounded-full"
              style={{ backgroundColor: "#107A92" }}
            />
            <span>{postLabel}</span>
          </div>
        ) : null}
      </div>

      <SignalChart
        rawSignal={prePoints}
        smoothSignal={postPoints}
        showRawSignal={hasPrePoints}
        rawBorderColor="#B776B9"
        smoothBorderColor="#107A92"
        peaks={[]}
        troughs={[]}
        maxSecs={Math.max(1, Math.ceil(maxSecs || 0))}
        height={224}
        tickStepSize={maxSecs <= 10 ? 0.5 : 5}
      />
    </SectionCard>
  );
}

export default function SixMinuteWalkAssessmentSection({
  preData = null,
  postData = null,
  t,
}) {
  const preLabel = t("profilePage.preLabel", "Pre");
  const postLabel = t("profilePage.postLabel", "Post");
  const routeTrace = useMemo(
    () => pickSixMinuteWalkRouteTrace(postData),
    [postData],
  );
  const preSignal = useMemo(
    () => pickSixMinuteWalkDetrendedSignal(preData).points,
    [preData],
  );
  const postSignal = useMemo(
    () => pickSixMinuteWalkDetrendedSignal(postData).points,
    [postData],
  );

  const walkSummaryCards = useMemo(
    () => [
      {
        label: t("profilePage.sixMinuteWalkDistance", "Distance"),
        value: formatDistanceValue(pickSixMinuteWalkDistanceMeters(postData)),
        unit: "m",
      },
      {
        label: t("profilePage.sixMinuteWalkTrackedDistance", "Total Tracked"),
        value: formatDistanceValue(
          pickSixMinuteWalkTotalTrackedDistanceMeters(postData),
        ),
        unit: "m",
      },
      {
        label: t(
          "profilePage.sixMinuteWalkTimeToRecording",
          "Time to recording",
        ),
        value: formatSecondsValue(pickSixMinuteWalkRecoveryDelayMs(postData)),
        unit: "s",
      },
    ],
    [postData, t],
  );

  const metricPairs = useMemo(
    () => [
      {
        key: "rr",
        pre: {
          label: buildPrefixedMetricLabel(t, preLabel, "RR"),
          value: formatValidatedMeasurement(
            "rr",
            pickSixMinuteWalkRespiratoryRate(preData),
          ),
          unit: "bpm",
        },
        post: {
          label: buildPrefixedMetricLabel(t, postLabel, "RR"),
          value: formatValidatedMeasurement(
            "rr",
            pickSixMinuteWalkRespiratoryRate(postData),
          ),
          unit: "bpm",
        },
      },
      {
        key: "hr",
        pre: {
          label: buildPrefixedMetricLabel(t, preLabel, "HR"),
          value: formatValidatedMeasurement(
            "hr",
            pickSixMinuteWalkHeartRate(preData),
          ),
          unit: "bpm",
        },
        post: {
          label: buildPrefixedMetricLabel(t, postLabel, "HR"),
          value: formatValidatedMeasurement(
            "hr",
            pickSixMinuteWalkHeartRate(postData),
          ),
          unit: "bpm",
        },
      },
      {
        key: "respiratory-quality",
        pre: {
          label: buildPrefixedMetricLabel(
            t,
            preLabel,
            t("profilePage.respiratoryQuality", "Respiratory quality"),
          ),
          value: formatStatusValue(
            pickSixMinuteWalkRespiratoryQuality(preData),
            t,
          ),
        },
        post: {
          label: buildPrefixedMetricLabel(
            t,
            postLabel,
            t("profilePage.respiratoryQuality", "Respiratory quality"),
          ),
          value: formatStatusValue(
            pickSixMinuteWalkRespiratoryQuality(postData),
            t,
          ),
        },
      },
      {
        key: "cardiac-quality",
        pre: {
          label: buildPrefixedMetricLabel(
            t,
            preLabel,
            t("profilePage.cardiacQuality", "Cardiac quality"),
          ),
          value: formatStatusValue(pickSixMinuteWalkCardiacQuality(preData), t),
        },
        post: {
          label: buildPrefixedMetricLabel(
            t,
            postLabel,
            t("profilePage.cardiacQuality", "Cardiac quality"),
          ),
          value: formatStatusValue(
            pickSixMinuteWalkCardiacQuality(postData),
            t,
          ),
        },
      },
    ],
    [postData, postLabel, preData, preLabel, t],
  );
  const comparisonCards = useMemo(
    () =>
      metricPairs.flatMap((pair) => [
        {
          key: `${pair.key}-pre`,
          ...pair.pre,
        },
        {
          key: `${pair.key}-post`,
          ...pair.post,
        },
      ]),
    [metricPairs],
  );

  return (
    <div className="flex flex-col gap-4">
      <section className="flex flex-wrap gap-4 w-full items-stretch">
        {walkSummaryCards.map((card) => (
          <SummaryCard
            key={card.label}
            label={card.label}
            value={card.value}
            unit={card.unit}
          />
        ))}
      </section>

      <div className="grid gap-4 grid-cols-2 md:grid-cols-4 w-full items-stretch">
        {comparisonCards.map((card) => (
          <SummaryCard
            key={card.key}
            label={card.label}
            value={card.value}
            unit={card.unit}
          />
        ))}
      </div>

      <SignalComparisonSection
        title={t(
          "profilePage.sixMinuteWalkSignalComparison",
          "Pre/post signal comparison",
        )}
        preLabel={preLabel}
        postLabel={postLabel}
        prePoints={preSignal}
        postPoints={postSignal}
        emptyLabel={t(
          "profilePage.sixMinuteWalkNoSignalComparison",
          "No comparable pre/post signals are available for this assessment.",
        )}
      />

      <div className="grid grid-cols-1 gap-5 xl:grid-cols-2">
        <SignalSection
          title={t("profilePage.sixMinuteWalkPreSignal", "Pre-walk signal")}
          points={preSignal}
          emptyLabel={t(
            "profilePage.noRespiratorySignal",
            "No respiratory signal is available for this recording.",
          )}
        />
        <SignalSection
          title={t("profilePage.sixMinuteWalkPostSignal", "Post-walk signal")}
          points={postSignal}
          emptyLabel={t(
            "profilePage.noRespiratorySignal",
            "No respiratory signal is available for this recording.",
          )}
        />
        <SectionCard title={t("profilePage.sixMinuteWalkRoute", "Walk route")}>
          {routeTrace.length ? (
            <SixMinuteWalkRouteChart
              points={routeTrace}
              fillContainer
              height={240}
            />
          ) : (
            <div className="rounded-lg bg-background px-4 py-10 text-center text-sm text-greyscale-3">
              {t(
                "profilePage.sixMinuteWalkNoRoute",
                "No route trace is available for this assessment.",
              )}
            </div>
          )}
        </SectionCard>
      </div>
    </div>
  );
}
